import { useAppState } from "../../context/appStateContext"
import { usePurchase } from "../../context/purchaseContext"
import { formatCurrency } from "../../utils/utils"
import useStrings from "../../localization/useStrings"
import styles from "./SocialProofFooter.module.scss"

const SocialProofFooter = () => {
  const bottomInset = useAppState().state.initialData?.bottomInset ?? 0
  const { state, actions } = usePurchase()
  const strings = useStrings()

  const hasTrial = state.selectedProduct.hasTrial
  const ctaTitle = hasTrial
    ? strings.socialProof.continueForFree
    : strings.socialProof.continue

  const locale = state.selectedPlan.yearlyProduct.priceLocale
  const yearlyPrice = formatCurrency(
    state.selectedPlan.yearlyProduct.price,
    locale,
  )
  const yearlyPerMonth = formatCurrency(
    state.selectedPlan.yearlyProduct.price / 12,
    locale,
  )
  const monthlyPrice = formatCurrency(
    state.selectedPlan.monthlyProduct.price,
    locale,
  )

  return (
    <div className={styles.container}>
      <div className={styles.background} style={{ bottom: -bottomInset }} />
      <div className={styles.selection}>
        <PricingCard
          title={strings.socialProof.twelveMonths}
          subtitle={strings.socialProof.yearlySubtitle(
            yearlyPerMonth,
            yearlyPrice,
          )}
          selected={state.yearlyItem.selected}
          onClick={() => actions.setYearlySelected(true)}
          badge={strings.socialProof.mostPopular}
        />
        <PricingCard
          title={strings.socialProof.oneMonth}
          subtitle={strings.socialProof.monthlySubtitle(monthlyPrice)}
          selected={state.monthlyItem.selected}
          onClick={() => actions.setYearlySelected(false)}
        />
      </div>
      <div className={styles.ctaContainer}>
        <button
          className={styles.ctaButton}
          onClick={() => actions.purchaseCurrent()}
        >
          {ctaTitle}
        </button>
      </div>
      <span className={styles.cancelText}>
        {strings.socialProof.cancelAnytime}
      </span>
    </div>
  )
}

interface PricingCardProps {
  title: string
  subtitle: string
  selected: boolean
  onClick: () => void
  badge?: string
}

const CheckCircle = () => (
  <svg
    className={styles.checkCircle}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.157 15.6569 15.657C17.1571 14.157 18 12.122 18 10C18 7.878 17.1571 5.843 15.6569 4.343C14.1566 2.843 12.1217 2 10 2C7.8783 2 5.8434 2.843 4.3431 4.343C2.8429 5.843 2 7.878 2 10C2 12.122 2.8429 14.157 4.3431 15.657C5.8434 17.157 7.8783 18 10 18ZM9.003 14L4.76 9.757L6.174 8.343L9.003 11.172L14.66 5.515L16.074 6.929L9.003 14Z"
      fill="url(#checkGradient)"
    />
    <defs>
      <linearGradient
        id="checkGradient"
        x1="10"
        y1="0"
        x2="10"
        y2="22"
        gradientUnits="userSpaceOnUse"
      >
        <stop
          className={styles.gradientStop1}
          offset="0.036"
          stopColor="#27AE60"
        />
        <stop
          className={styles.gradientStop2}
          offset="0.443"
          stopColor="#2B5FF5"
        />
        <stop className={styles.gradientStop3} offset="1" stopColor="#0321BC" />
      </linearGradient>
    </defs>
  </svg>
)

const EmptyCircle = () => (
  <svg
    className={styles.emptyCircle}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.157 15.6569 15.657C17.1571 14.157 18 12.122 18 10C18 7.878 17.1571 5.843 15.6569 4.343C14.1566 2.843 12.1217 2 10 2C7.8783 2 5.8434 2.843 4.3431 4.343C2.8429 5.843 2 7.878 2 10C2 12.122 2.8429 14.157 4.3431 15.657C5.8434 17.157 7.8783 18 10 18Z"
      fill="currentColor"
    />
  </svg>
)

const PricingCard = ({
  title,
  subtitle,
  selected,
  onClick,
  badge,
}: PricingCardProps) => {
  return (
    <button
      className={`${styles.pricingCard} ${selected ? styles.selected : ""}`}
      onClick={onClick}
    >
      {badge && <div className={styles.badge}>{badge}</div>}
      <div className={styles.checkIcon}>
        {selected ? <CheckCircle /> : <EmptyCircle />}
      </div>
      <div className={styles.pricingContent}>
        <span className={styles.pricingTitle}>{title}</span>
        <span className={styles.pricingSubtitle}>{subtitle}</span>
      </div>
    </button>
  )
}

export default SocialProofFooter
